import React, { useCallback, useEffect, useMemo, useState } from "react";
import AlertMessage from "../../common/actions/AlertMessage";
import Loader from "../../common/loaders/Loader";
import DownloadButton from "../../common/buttons/DownloadButton";
import DataTable from "../../common/shared/DataTable";
import PageTitle from "../../common/shared/PageTitle";
import MoneyIcon from "../../common/icons/moneyIcon";
import {
  allSummitPayments,
  approveSummitPayment,
} from "../../redux/api/payment/summit_api";
import saveDownload from "../../utils/downloadFile";

const money = (value) =>
  `GH₵${Number(value || 0).toLocaleString("en-GH", {
    minimumFractionDigits: 2,
  })}`;

export default function SummitPayments() {
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const [method, setMethod] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await allSummitPayments();
      if (result.error) {
        setError(result.error);
      } else {
        setPayments(result.data.payments);
        setError("");
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const filtered = useMemo(
    () =>
      payments.filter(
        (payment) =>
          (!method || payment.method === method) &&
          (!status || payment.status === status) &&
          `${payment.name} ${payment.bar_number} ${payment.reference}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [payments, method, status, search],
  );
  const paid = payments.filter((p) => p.status === "Paid");
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      width: 130,
      sortable: false,
      renderCell: ({ row }) =>
        row.method === "Manual" && row.status === "Pending" ? (
          <button
            className="btn btn-sm"
            disabled={busy}
            onClick={() => {
              setSelected(row);
              setNotes("");
            }}
          >
            Approve
          </button>
        ) : null,
    },
    { field: "name", headerName: "Member", flex: 1, minWidth: 150 },
    { field: "bar_number", headerName: "Bar number", width: 130 },
    {
      field: "amount",
      headerName: "Ticket",
      width: 130,
      valueFormatter: (params) => money(params.value),
    },
    {
      field: "charges",
      headerName: "Charges",
      width: 130,
      valueFormatter: (params) => money(params.value),
    },
    {
      field: "total",
      headerName: "Total",
      width: 130,
      valueFormatter: (params) => money(params.value),
    },
    { field: "method", headerName: "Method", width: 130 },
    { field: "status", headerName: "Status", width: 180 },
    { field: "reference", headerName: "Reference", width: 180 },
    {
      field: "date_paid",
      headerName: "Payment date",
      width: 180,
      valueFormatter: (params) =>
        params.value
          ? new Date(params.value).toLocaleString("en-GB", {
              timeZone: "Africa/Accra",
            })
          : "—",
    },
    { field: "approved_by", headerName: "Approved by", width: 150 },
  ];

  const approve = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await approveSummitPayment(selected.id, notes.trim());
      if (result.error) {
        setError(result.error);
      } else {
        setSelected(null);
        setNotes("");
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <AlertMessage type="error" message={error} />}
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="flex justify-between p-4">
            <div className="flex items-center">
              <PageTitle
                title="Boardroom Governance Summit 2026"
                icon={<MoneyIcon />}
              />
            </div>
            <div className="flex items-center gap-4">
              <DownloadButton
                onClick={() =>
                  saveDownload(filtered, "Boardroom Summit Payments")
                }
                disabled={!filtered.length}
              />
              <button className="btn btn-outline" onClick={load}>
                Refresh
              </button>
            </div>
          </div>
          <p className="px-4">October 7, 2026 · Labadi Beach Hotel</p>
          <div className="flex flex-wrap gap-6 p-4">
            <p>
              <strong>{paid.length}</strong> paid tickets
            </p>
            <p>
              Ticket revenue:{" "}
              <strong>
                {money(
                  paid.reduce(
                    (sum, payment) => sum + Number(payment.amount),
                    0,
                  ),
                )}
              </strong>
            </p>
            <p>
              Charges:{" "}
              <strong>
                {money(
                  paid.reduce(
                    (sum, payment) => sum + Number(payment.charges),
                    0,
                  ),
                )}
              </strong>
            </p>
            <p>
              Confirmed total:{" "}
              <strong>
                {money(
                  paid.reduce((sum, payment) => sum + Number(payment.total), 0),
                )}
              </strong>
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3 p-4">
            <label>
              Search
              <input
                className="block border rounded p-2"
                placeholder="Name, bar number or reference"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label>
              Method
              <select
                className="block border rounded p-2"
                value={method}
                onChange={(event) => setMethod(event.target.value)}
              >
                <option value="">All methods</option>
                <option>Electronic</option>
                <option>Manual</option>
              </select>
            </label>
            <label>
              Status
              <select
                className="block border rounded p-2"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">All statuses</option>
                {[
                  "Pending",
                  "Paid",
                  "Unsuccessful",
                  "Needs reconciliation",
                ].map((paymentStatus) => (
                  <option key={paymentStatus}>{paymentStatus}</option>
                ))}
              </select>
            </label>
          </div>
          {selected && (
            <form
              onSubmit={approve}
              className="border rounded p-5 mx-4 my-6"
              aria-label="Confirm manual payment"
            >
              <h2 className="text-lg font-bold">
                Confirm manual payment for {selected.name}
              </h2>
              <p>
                {selected.bar_number} · {money(selected.total)}
              </p>
              <label className="block my-3">
                Payment reference or note
                <input
                  className="block border rounded p-2 w-full"
                  required
                  maxLength={255}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
              <p className="mb-3">
                Confirm only after the Secretariat has received the full amount.
              </p>
              <button
                className="btn btn-primary mr-3"
                disabled={busy || !notes.trim()}
              >
                {busy ? "Confirming…" : "Confirm payment"}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                disabled={busy}
                onClick={() => setSelected(null)}
              >
                Cancel
              </button>
            </form>
          )}
          <DataTable rows={filtered} columns={columns} />
          {!filtered.length && (
            <p className="p-5">No payments match these filters.</p>
          )}
        </>
      )}
    </>
  );
}
